import { action, computed, observable } from 'mobx';
import UiState from '../../stores/UIState';
import { RouterStore } from '@estee/elc-router';
import TransactionRepository from '../../repositories/TransactionRepository';
import ConfigRepository from '../../repositories/ConfigRepository';
import Routes, { ExternalRoute } from '../../application/routes/Routes';
import CartItem from '../../domain/entities/Cart/CartItem';
import Order from '../../domain/entities/Order/Order';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import { ADDRESS_TYPE_ID, DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';

interface IShoppingBagConfig {
  configRepository: ConfigRepository;
  routes: Routes;
  stateRouter: RouterStore;
  transactionRepository: TransactionRepository;
  uiState: UiState;
}

interface IShoppingBagViewControllerData {
  baseUrl: string;
  cartUrl: string;
  defaultShippingDaysTimeValue?: number;
  showSplitShipments: boolean;
  isBopisOrCourier: boolean;
  isShoppingBagOpen: boolean;
  locale: string;
  orderSummary: Order;
  shipGroupedCartItems: Map<string, CartItem[]>;
  showFreeLabelAsPrice: boolean;
}

class ShoppingBagViewController {

  private configRepository: ConfigRepository;

  private routes: Routes;
  
  private stateRouter: RouterStore;
  
  private transactionRepository: TransactionRepository;
  
  private uiState: UiState;

  @observable isShoppingBagOpen: boolean;
  
  constructor(config: IShoppingBagConfig) {
    this.configRepository = config.configRepository;
    this.routes = config.routes;
    this.stateRouter = config.stateRouter;
    this.transactionRepository = config.transactionRepository;
    this.uiState = config.uiState;
  }

  @action
  async loaded(): Promise<void> {
    this.setInitialShoppingBagState();

    await this.configRepository.getConfig();
    await this.transactionRepository.getTransaction();
  }

  @action
  setIsShoppingBagOpen(isShoppingBagOpen: boolean): void {
    this.isShoppingBagOpen = isShoppingBagOpen;
  }

  @action
  toggleShoppingBagOpen = (): void => {
    this.isShoppingBagOpen = !this.isShoppingBagOpen;
  };

  @action
  private setInitialShoppingBagState = (): void => {
    this.setIsShoppingBagOpen(true);
  };

  @computed
  get enableShipmentSplits(): boolean {
    const { config } = this.configRepository;
    const addressType = this.transactionRepository.transaction?.shippingAddress?.attributes?.[FIELD_TYPE.ADDRESS_TYPE];

    const hasShipmentSplitsEnabled = config?.isSplitShipmentEnabled && config?.isEstimatedDeliveryDateEnabled;
    const isShippingTypeEligible = addressType && addressType !== ADDRESS_TYPE_ID.SHIPPING;

    return hasShipmentSplitsEnabled && isShippingTypeEligible;
  }

  @computed
  get showSplitShipments(): boolean {
    const deliveryOption = this.transactionRepository.transaction?.deliveryOption;
    return this.enableShipmentSplits && (deliveryOption === DeliveryOptions.HOME_DELIVERY);
  }

  @computed
  get isBopisOrCourier(): boolean {
    const shipMethodID = this.transactionRepository.transaction?.selectedShippingMethodId;

    return shipMethodID && (shipMethodID === SHIPPING_METHODS.BOPIS || shipMethodID === SHIPPING_METHODS.COURIER);
  }

  @computed
  get data(): IShoppingBagViewControllerData {
    return {
      baseUrl: this.configRepository.config?.baseUrl,
      cartUrl: this.routes.getExternalRoute(ExternalRoute.CART),
      defaultShippingDaysTimeValue: this.transactionRepository.transaction?.getShippingMethodById(this.transactionRepository.transaction?.selectedShippingMethodId)?.leadTimeValue,
      isBopisOrCourier: this.isBopisOrCourier,
      isShoppingBagOpen: this.isShoppingBagOpen,
      locale: this.configRepository.config?.locale,
      orderSummary: this.transactionRepository.transaction?.orderSummary,
      shipGroupedCartItems: this.transactionRepository.transaction?.cart.shipGroupedCartItems,
      showFreeLabelAsPrice: this.configRepository.config?.showFreeLabelAsPrice,
      showSplitShipments: this.showSplitShipments,
    };
  }
}

export default ShoppingBagViewController;
